export type DiffOp = 'equal' | 'add' | 'remove';

export interface DiffRow {
  op: DiffOp;
  /** 1-based line number in the old text; absent for added lines. */
  oldLine?: number;
  /** 1-based line number in the new text; absent for removed lines. */
  newLine?: number;
  text: string;
}

export interface DiffStats {
  added: number;
  removed: number;
}

export interface DiffHunk {
  rows: DiffRow[];
}

/** Guard against pathological inputs: a full LCS table is O(n·m) memory. */
const MAX_LCS_LINES = 2500;

function splitLines(text: string): string[] {
  return text.replace(/\r\n?/g, '\n').split('\n');
}

/**
 * Longest common subsequence over whole lines.
 *
 * Common prefixes and suffixes are stripped first, which is what makes this fast
 * enough for real programs: an edited PLC block usually shares hundreds of
 * identical leading declaration lines with its predecessor.
 */
export function diffLines(oldText: string, newText: string): DiffRow[] {
  const oldLines = splitLines(oldText);
  const newLines = splitLines(newText);

  const rows: DiffRow[] = [];
  let start = 0;
  while (start < oldLines.length && start < newLines.length && oldLines[start] === newLines[start]) {
    rows.push({ op: 'equal', oldLine: start + 1, newLine: start + 1, text: oldLines[start] });
    start += 1;
  }

  let oldEnd = oldLines.length;
  let newEnd = newLines.length;
  const suffix: DiffRow[] = [];
  while (oldEnd > start && newEnd > start && oldLines[oldEnd - 1] === newLines[newEnd - 1]) {
    oldEnd -= 1;
    newEnd -= 1;
    suffix.unshift({ op: 'equal', oldLine: oldEnd + 1, newLine: newEnd + 1, text: oldLines[oldEnd] });
  }

  const oldMid = oldLines.slice(start, oldEnd);
  const newMid = newLines.slice(start, newEnd);

  if (oldMid.length > MAX_LCS_LINES || newMid.length > MAX_LCS_LINES) {
    // Too large to align precisely; report it as a wholesale replacement rather
    // than freezing the tab.
    oldMid.forEach((text, i) => rows.push({ op: 'remove', oldLine: start + i + 1, text }));
    newMid.forEach((text, i) => rows.push({ op: 'add', newLine: start + i + 1, text }));
    return rows.concat(suffix);
  }

  const n = oldMid.length;
  const m = newMid.length;
  const table: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      table[i][j] = oldMid[i] === newMid[j]
        ? table[i + 1][j + 1] + 1
        : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (oldMid[i] === newMid[j]) {
      rows.push({ op: 'equal', oldLine: start + i + 1, newLine: start + j + 1, text: oldMid[i] });
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      rows.push({ op: 'remove', oldLine: start + i + 1, text: oldMid[i] });
      i += 1;
    } else {
      rows.push({ op: 'add', newLine: start + j + 1, text: newMid[j] });
      j += 1;
    }
  }
  while (i < n) {
    rows.push({ op: 'remove', oldLine: start + i + 1, text: oldMid[i] });
    i += 1;
  }
  while (j < m) {
    rows.push({ op: 'add', newLine: start + j + 1, text: newMid[j] });
    j += 1;
  }

  return rows.concat(suffix);
}

export function summarizeDiff(rows: DiffRow[]): DiffStats {
  let added = 0;
  let removed = 0;
  for (const row of rows) {
    if (row.op === 'add') added += 1;
    else if (row.op === 'remove') removed += 1;
  }
  return { added, removed };
}

/**
 * Collapse long stretches of unchanged lines into hunks.
 *
 * A 400-line program with a three-line safety fix should read as a three-line
 * fix; showing 397 identical lines around it hides the thing being reviewed.
 */
export function buildHunks(rows: DiffRow[], context = 3): DiffHunk[] {
  const changedAt = new Set<number>();
  rows.forEach((row, index) => {
    if (row.op === 'equal') return;
    for (let offset = -context; offset <= context; offset += 1) {
      const target = index + offset;
      if (target >= 0 && target < rows.length) changedAt.add(target);
    }
  });

  if (changedAt.size === 0) return [];

  const hunks: DiffHunk[] = [];
  let current: DiffRow[] = [];
  for (let index = 0; index < rows.length; index += 1) {
    if (changedAt.has(index)) {
      current.push(rows[index]);
    } else if (current.length > 0) {
      hunks.push({ rows: current });
      current = [];
    }
  }
  if (current.length > 0) hunks.push({ rows: current });
  return hunks;
}
